import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { isStaffReminderTemplate, reminderEmailKind } from '@notifi/shared-types/email-kinds';
import { HintTooltip } from '@/components/molecules';
import { EMAIL_KIND_LABEL } from '@/types/emailTemplate';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import { Textarea } from '@/components/atoms/textarea';
import { cn } from '@/lib/utils';
import {
  REMINDER_CHANNELS,
  type ReminderChannel,
  type TemplateConfig,
} from '@/types/reminder-policy';

interface TemplateEditorProps {
  value: TemplateConfig[];
  onChange: (next: TemplateConfig[]) => void;
  readOnly?: boolean;
  /** The policy's reminder type — decides which EMAIL steps render through a design. */
  reminderType?: string;
}

export function TemplateEditor({ value, onChange, readOnly, reminderType }: TemplateEditorProps) {
  const [activeChannel, setActiveChannel] = useState<ReminderChannel>('SMS');

  function update(index: number, next: TemplateConfig) {
    onChange(value.map((tpl, i) => (i === index ? next : tpl)));
  }

  function remove(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function add() {
    onChange([...value, { key: '', channel: activeChannel, subject: undefined, body: '' }]);
  }

  const channelTemplates = value
    .map((tpl, originalIndex) => ({ tpl, originalIndex }))
    .filter(({ tpl }) => tpl.channel === activeChannel);

  const countFor = (ch: ReminderChannel) => value.filter((t) => t.channel === ch).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-medium">Templates</h3>
          <HintTooltip>
            SMS and voice copy per step. Customer emails are designed in Designs → Emails; this list
            only picks which design each email step sends.
          </HintTooltip>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={readOnly}>
          <Plus className="mr-1 size-4" /> Add template
        </Button>
      </div>

      <Tabs value={activeChannel} onValueChange={(v) => setActiveChannel(v as ReminderChannel)}>
        <TabsList variant="line" className="w-full justify-start">
          {REMINDER_CHANNELS.map((ch) => (
            <TabsTrigger key={ch} value={ch}>
              {ch}
              {countFor(ch) > 0 && (
                <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
                  {countFor(ch)}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {REMINDER_CHANNELS.map((ch) => (
          <TabsContent key={ch} value={ch} className="mt-3">
            {channelTemplates.length === 0 ? (
              <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No {ch} templates. Click &ldquo;Add template&rdquo; to create one.
              </div>
            ) : (
              <div className="space-y-3">
                {channelTemplates.map(({ tpl, originalIndex }) => (
                  <TemplateCard
                    key={originalIndex}
                    tpl={tpl}
                    reminderType={reminderType}
                    readOnly={readOnly}
                    onUpdate={(next) => update(originalIndex, next)}
                    onRemove={() => remove(originalIndex)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function TemplateCard({
  tpl,
  reminderType,
  readOnly,
  onUpdate,
  onRemove,
}: {
  tpl: TemplateConfig;
  reminderType?: string;
  readOnly?: boolean;
  onUpdate: (next: TemplateConfig) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(() => !tpl.key);
  // A customer EMAIL step renders through its email design: the copy lives
  // there, not here. The office flags stay editable (they go to the team).
  const designKind =
    tpl.channel === 'EMAIL' && reminderType ? reminderEmailKind(reminderType, tpl.key) : null;
  const staffAudience =
    tpl.channel === 'EMAIL' && !!reminderType && isStaffReminderTemplate(reminderType, tpl.key);
  const bodyLen = tpl.body.length;
  const segments = bodyLen === 0 ? 0 : bodyLen <= 160 ? 1 : Math.ceil(bodyLen / 153);
  const displayName = tpl.key
    ? tpl.key
        .replace(/[-_]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : 'New template';

  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-180',
            )}
          />
          <span className="min-w-0 truncate text-sm font-medium">{displayName}</span>
          {designKind && (
            <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
              Designed email
            </span>
          )}
          {staffAudience && (
            <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground">
              Goes to your team
            </span>
          )}
          {!expanded && tpl.channel === 'SMS' && bodyLen > 0 && (
            <span
              className={cn(
                'shrink-0 text-xs',
                bodyLen > 160 ? 'text-amber-500' : 'text-muted-foreground',
              )}
            >
              {bodyLen} chars{segments > 1 ? ` · ${segments} seg` : ''}
            </span>
          )}
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onRemove}
          disabled={readOnly}
          aria-label="Remove template"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>

      {expanded && designKind && (
        <div className="space-y-2 border-t border-border px-3 pb-3 pt-3 text-sm">
          <p className="text-muted-foreground">
            This step sends the{' '}
            <span className="font-medium text-foreground">{EMAIL_KIND_LABEL[designKind]}</span>{' '}
            design: subject, wording, button and legal footer are edited there, and it is branded
            per business unit.
          </p>
          <Link
            to="/dashboard/settings/document-templates"
            search={{ tab: 'emails', kind: designKind }}
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
          >
            Open in Designs → Emails
            <ExternalLink className="size-3.5" />
          </Link>
        </div>
      )}

      {expanded && !designKind && (
        <div className="space-y-3 border-t border-border px-3 pb-3 pt-3">
          {staffAudience && (
            <p className="text-xs text-muted-foreground">
              Internal note — sent to your company email (Settings → Company), never to the
              customer.
            </p>
          )}
          <div className="space-y-1">
            <Label>Name</Label>
            <Input
              value={tpl.key}
              onChange={(e) => onUpdate({ ...tpl, key: e.target.value })}
              placeholder="e.g. initial-reminder, followup"
              disabled={readOnly}
            />
          </div>

          {tpl.channel === 'EMAIL' && (
            <div className="space-y-1">
              <Label>Subject</Label>
              <Input
                value={tpl.subject ?? ''}
                onChange={(e) => onUpdate({ ...tpl, subject: e.target.value || undefined })}
                placeholder="Email subject line"
                disabled={readOnly}
              />
            </div>
          )}

          <div className="space-y-1">
            <Label>Body</Label>
            <Textarea
              rows={tpl.channel === 'EMAIL' ? 5 : 3}
              value={tpl.body}
              onChange={(e) => onUpdate({ ...tpl, body: e.target.value })}
              placeholder="Message body — use {{contact.firstName}}, {{links.confirmUrl}}, etc."
              disabled={readOnly}
            />
            {tpl.channel === 'SMS' && (
              <p
                className={cn(
                  'text-right text-xs',
                  bodyLen > 160 ? 'text-amber-500' : 'text-muted-foreground',
                )}
              >
                {bodyLen} chars{segments > 1 ? ` · ${segments} segments` : ''}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
